import React from "react";
import { SectionTitle } from "./FormField";
import { CopyButton } from "./CopyButton";
import { getCustomerUrl } from "../../../../constants";
import { FaSms, FaLine, FaGift, FaPaperPlane } from "react-icons/fa";

export const OrderNotifications = ({
  orderInfo,
  callApi,
  setLoading,
}) => {
  const hasBuyerSms = Boolean(orderInfo?.phone);
  const hasBuyerLine = Boolean(orderInfo?.lineId);
  const hasBuyForSms = Boolean(
    (orderInfo?.isBuyForSomeoneElse || orderInfo?.buyFor?.phone) &&
    (orderInfo?.buyFor?.phone || orderInfo?.buyFor?.buyForPhone)
  );

  if (!hasBuyerSms && !hasBuyerLine && !hasBuyForSms) return null;

  const handleSendNotification = async (type, confirmTitle, confirmDetails) => {
    const confirmSend = window.confirm(
      `ยืนยันการ${confirmTitle}หรือไม่?\n\n${confirmDetails}`
    );
    if (!confirmSend) return;

    setLoading(true);
    try {
      const res = await callApi(
        "utils-notifyOrderStatus",
        {
          orderId: orderInfo.id,
          type: type,
        },
        { notShowLoading: true }
      );
      if (res?.toastMessage) {
        alert(res.toastMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const ordersLink = getCustomerUrl("/orders");
  const buyerSmsText = orderInfo?.isGuest
    ? `คุณ ${orderInfo?.firstName || "-"} ได้ทำการสั่งซื้อสลากกับวันดี คำสั่งซื้อ #${orderInfo?.shortId || orderInfo?.id?.slice(-5).toUpperCase() || ""} ${orderInfo?.status === "APPROVE" ? "สำเร็จ" : "โปรดตรวจสอบการสั่งซื้อ"} ดูเพิ่มเติมที่ ${ordersLink}`
    : `คำสั่งซื้อ #${orderInfo?.shortId || orderInfo?.id?.slice(-5).toUpperCase() || ""} ${orderInfo?.status === "APPROVE" ? "สำเร็จ" : "รออนุมัติ/โปรดตรวจสอบ"} ดูเพิ่มเติมที่ ${ordersLink}`;

  const buyForSmsText = `คุณ${orderInfo?.firstName || "-"} สั่งซื้อสลากให้คุณ${orderInfo?.buyFor?.firstName || "-"} จำนวน ${orderInfo?.size || 0} ใบ #${orderInfo?.shortId || ""} สำเร็จ ดูสลากได้ที่แอดมิน https://lin.ee/qmOmnRx`;

  return (
    <div>
      <SectionTitle title="การส่งข้อความแจ้งเตือนลูกค้า" />

      <div className="flex flex-col gap-3">
        {/* Card 1: SMS Notification for Buyer */}
        {hasBuyerSms && (
          <div className="bg-white border border-slate-200 rounded-md p-4 shadow-xs flex flex-col gap-3">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 pb-2.5">
              <div className="flex items-center gap-2">
                <span className="w-7 h-7 rounded-md bg-blue/10 text-blue flex items-center justify-center text-xs">
                  <FaSms />
                </span>
                <div>
                  <h4 className="font-bold text-sm text-slate-800">
                    แจ้งเตือนสถานะคำสั่งซื้อผู้ซื้อทาง SMS
                  </h4>
                  <p className="text-[11px] font-medium text-slate-500">
                    ส่งข้อความ SMS แจ้งสถานะคำสั่งซื้อไปยังเบอร์โทรศัพท์ของลูกค้า
                  </p>
                </div>
              </div>
              <span className="text-xs font-bold text-slate-700 bg-slate-100 px-2.5 py-1 rounded-md border border-slate-200">
                เบอร์โทร: {orderInfo?.phone}
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs bg-slate-50 p-2.5 rounded-md border border-slate-200">
              <div>
                <span className="text-slate-500">ผู้รับ SMS:</span>{" "}
                <span className="font-bold text-slate-800">
                  คุณ {orderInfo?.firstName || "-"} {orderInfo?.lastName || ""}
                </span>
              </div>
              <div>
                <span className="text-slate-500">เบอร์ปลายทาง:</span>{" "}
                <span className="font-bold text-slate-800">{orderInfo?.phone}</span>
              </div>
            </div>

            <div className="flex flex-col gap-1">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-slate-600">
                  ข้อความที่จะส่ง SMS:
                </span>
                <CopyButton text={buyerSmsText} label="คัดลอกข้อความ" iconOnly={false} />
              </div>
              <p className="font-mono text-xs leading-relaxed bg-slate-100/70 p-2.5 rounded-md border border-slate-200 text-slate-800 select-all break-words">
                {buyerSmsText}
              </p>
            </div>

            <div className="flex justify-end pt-1">
              <button
                type="button"
                className="bg-blue hover:opacity-90 text-white text-xs font-bold py-2 px-4 rounded-md flex items-center gap-2 transition-all shadow-xs cursor-pointer"
                onClick={() => {
                  const buyerName = `${orderInfo?.firstName || ""} ${orderInfo?.lastName || ""}`.trim();
                  const buyerPhone = orderInfo?.phone || "-";
                  handleSendNotification(
                    "sms",
                    "ส่ง SMS แจ้งสถานะคำสั่งซื้อให้ผู้ซื้อ",
                    `• ส่งถึง: คุณ ${buyerName} (${buyerPhone})\n• ข้อความ:\n"${buyerSmsText}"`
                  );
                }}
              >
                <FaPaperPlane className="text-[10px]" />
                <span>ส่ง SMS แจ้งผู้ซื้อ (คุณ {orderInfo?.firstName || "ผู้ซื้อ"})</span>
              </button>
            </div>
          </div>
        )}

        {/* Card 2: LINE Notification for Buyer */}
        {hasBuyerLine && (
          <div className="bg-white border border-slate-200 rounded-md p-4 shadow-xs flex flex-col gap-3">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 pb-2.5">
              <div className="flex items-center gap-2">
                <span className="w-7 h-7 rounded-md bg-green/10 text-green flex items-center justify-center text-xs">
                  <FaLine />
                </span>
                <div>
                  <h4 className="font-bold text-sm text-slate-800">
                    แจ้งเตือนสถานะคำสั่งซื้อทาง LINE
                  </h4>
                  <p className="text-[11px] font-medium text-slate-500">
                    ส่งข้อความไปยัง LINE ของสมาชิก
                  </p>
                </div>
              </div>
              <span className="text-xs font-bold text-green bg-green/10 px-2.5 py-1 rounded-md border border-green/20">
                LINE เชื่อมต่อแล้ว
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs bg-slate-50 p-2.5 rounded-md border border-slate-200">
              <div>
                <span className="text-slate-500">ผู้รับ LINE:</span>{" "}
                <span className="font-bold text-slate-800">
                  คุณ {orderInfo?.firstName || "-"} {orderInfo?.lastName || ""}
                </span>
              </div>
              <div>
                <span className="text-slate-500">LINE User ID:</span>{" "}
                <span className="font-bold font-mono text-slate-800">{orderInfo?.lineId}</span>
              </div>
            </div>

            <div className="text-xs bg-slate-50 p-3 rounded-md border border-slate-200 flex flex-col gap-1 text-slate-700 font-mono leading-relaxed select-all">
              <p className="font-bold text-slate-900 font-sans mb-1">
                💬 ตัวอย่างเนื้อหาการ์ด LINE:
              </p>
              <p>• <strong>คำสั่งซื้อ:</strong> #{orderInfo?.shortId || orderInfo?.id?.slice(-5).toUpperCase() || ""}</p>
              <p>• <strong>รหัสสมาชิก:</strong> HT{orderInfo?.userId || "-"}</p>
              <p>• <strong>จำนวนสลาก:</strong> {orderInfo?.size || orderInfo?.lotteryIds?.length || 0} ใบ</p>
              <p>• <strong>ยอดรวม:</strong> {orderInfo?.totalPrice ? Number(orderInfo.totalPrice).toLocaleString() : 0} บาท</p>
              <p>• <strong>สถานะ:</strong> {orderInfo?.status === "APPROVE" ? "อนุมัติสำเร็จ" : orderInfo?.status === "PAID" ? "รออนุมัติ" : orderInfo?.status === "PENDING" ? "รอชำระเงิน" : orderInfo?.status || "-"}</p>
              <p>• <strong>ช่องทาง:</strong> {orderInfo?.paymentChannel === "qr" ? "QR Code" : "โอนผ่านธนาคาร"}</p>
            </div>

            <div className="flex justify-end pt-1">
              <button
                type="button"
                className="bg-green hover:opacity-90 text-white text-xs font-bold py-2 px-4 rounded-md flex items-center gap-2 transition-all shadow-xs cursor-pointer"
                onClick={() => {
                  const buyerName = `${orderInfo?.firstName || ""} ${orderInfo?.lastName || ""}`.trim();
                  handleSendNotification(
                    "line",
                    "ส่งข้อความ LINE แจ้งสถานะคำสั่งซื้อ",
                    `• ส่งถึง: คุณ ${buyerName}\n• LINE User ID: ${orderInfo?.lineId}\n• รหัสคำสั่งซื้อ: #${orderInfo?.shortId || orderInfo?.id?.slice(-5).toUpperCase() || ""}`
                  );
                }}
              >
                <FaPaperPlane className="text-[10px]" />
                <span>ส่งข้อความ LINE แจ้งผู้ซื้อ</span>
              </button>
            </div>
          </div>
        )}

        {/* Card 3: Buy For Someone Else SMS Notification */}
        {hasBuyForSms && (
          <div className="bg-white border border-slate-200 rounded-md p-4 shadow-xs flex flex-col gap-3">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 pb-2.5">
              <div className="flex items-center gap-2">
                <span className="w-7 h-7 rounded-md bg-purple-50 text-[#9333ea] flex items-center justify-center text-xs">
                  <FaGift />
                </span>
                <div>
                  <h4 className="font-bold text-sm text-slate-800">
                    แจ้งเตือนผู้รับสลากฝากซื้อทาง SMS
                  </h4>
                  <p className="text-[11px] font-medium text-slate-500">
                    ส่งข้อความ SMS แจ้งผู้รับฝากซื้อว่ามีผู้สั่งซื้อสลากมอบให้
                  </p>
                </div>
              </div>
              <span className="text-xs font-bold text-[#9333ea] bg-purple-50 px-2.5 py-1 rounded-md border border-purple-200">
                ผู้รับ: {orderInfo?.buyFor?.firstName || "-"} {orderInfo?.buyFor?.lastName || ""}
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs bg-slate-50 p-2.5 rounded-md border border-slate-200">
              <div>
                <span className="text-slate-500">ผู้สั่งซื้อให้:</span>{" "}
                <span className="font-bold text-slate-800">
                  คุณ {orderInfo?.firstName || "-"} {orderInfo?.lastName || ""}
                </span>
              </div>
              <div>
                <span className="text-slate-500">เบอร์โทรผู้รับ:</span>{" "}
                <span className="font-bold text-slate-800">
                  {orderInfo?.buyFor?.phone || orderInfo?.buyFor?.buyForPhone || "-"}
                </span>
              </div>
            </div>

            <div className="flex flex-col gap-1">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-slate-600">
                  ข้อความที่จะส่ง SMS:
                </span>
                <CopyButton text={buyForSmsText} label="คัดลอกข้อความ" iconOnly={false} />
              </div>
              <p className="font-mono text-xs leading-relaxed bg-slate-100/70 p-2.5 rounded-md border border-slate-200 text-slate-800 select-all break-words">
                {buyForSmsText}
              </p>
            </div>

            <div className="flex justify-end pt-1">
              <button
                type="button"
                className="bg-[#9333ea] hover:opacity-90 text-white text-xs font-bold py-2 px-4 rounded-md flex items-center gap-2 transition-all shadow-xs cursor-pointer"
                onClick={() => {
                  const recipientName = `${orderInfo?.buyFor?.firstName || ""} ${orderInfo?.buyFor?.lastName || ""}`.trim();
                  const recipientPhone = orderInfo?.buyFor?.phone || orderInfo?.buyFor?.buyForPhone || "-";
                  handleSendNotification(
                    "buyForSomeoneElse",
                    "ส่ง SMS ให้ผู้รับฝากซื้อ",
                    `• ส่งถึง: คุณ ${recipientName} (${recipientPhone})\n• ข้อความ:\n"${buyForSmsText}"`
                  );
                }}
              >
                <FaPaperPlane className="text-[10px]" />
                <span>ส่ง SMS ให้ผู้รับ (คุณ {orderInfo?.buyFor?.firstName || "ผู้รับ"})</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
